<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ContentHeader',
  components: {
    Action: () => import('@/components/general/Action'),
    Icon: () => import('@/components/general/Icon'),
    Logo: () => import('@/components/general/Logo')
  },

  props: {
    title: {
      type: String,
      default: null
    },

    description: {
      type: String,
      default: null
    },

    tag: {
      type: String,
      default: null
    },

    background: {
      type: String,
      default: null
    },

    back: {
      type: Boolean,
      default: false
    },

    backText: {
      type: String,
      default: 'Voltar'
    },

    backUrl: {
      type: Object,
      default: null
    },

    thick: {
      type: Boolean,
      default: false
    },

    retract: {
      type: Boolean,
      default: false
    },

    fullscreen: {
      type: Boolean,
      default: false
    },

    fixed: {
      type: Boolean,
      default: false
    },

    sticky: {
      type: Boolean,
      default: false
    },

    lightTheme: {
      type: Boolean,
      default: false
    },

    darkTheme: {
      type: Boolean,
      default: false
    },

    fitTitle: {
      type: Boolean,
      default: false
    },

    float: {
      type: Boolean,
      default: false
    },

    classroom: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      retractActive: false,
      stuck: false,
      parallaxOffset: 0,
      reducedMotion: false,
      reducedMotionMq: null,
      objectStyle: {
        backgroundImage: 'auto'
      }
    }
  },

  computed: {
    ...mapGetters([ 'isFullscreen' ]),

    hasParallax () {
      return Boolean(this.background) && !this.reducedMotion && !this.float && !this.thick
    },

    parallaxStyle () {
      if (!this.background) return null

      return {
        backgroundImage: `url('${this.background}')`,
        transform: this.hasParallax ? `translate3d(0, ${this.parallaxOffset}px, 0)` : 'none'
      }
    }
  },

  watch: {
    title () {
      this.$nextTick(() => {
        !this.$media.mobile && this.calculateTitleFontSize()
      })
    }
  },

  mounted () {
    this.title && !this.$media.mobile && this.calculateTitleFontSize()

    if (this.sticky) {
      this.handleStickyScroll()
      window.addEventListener('scroll', this.handleStickyScroll, { passive: true })
    }

    if (this.background && window.matchMedia) {
      this.reducedMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.reducedMotion = this.reducedMotionMq.matches

      if (this.reducedMotionMq.addEventListener) {
        this.reducedMotionMq.addEventListener('change', this.onReducedMotionChange)
      } else if (this.reducedMotionMq.addListener) {
        this.reducedMotionMq.addListener(this.onReducedMotionChange)
      }

      window.addEventListener('scroll', this.handleParallaxScroll, { passive: true })
      this.handleParallaxScroll()
    }
  },

  beforeDestroy () {
    if (this.sticky) {
      window.removeEventListener('scroll', this.handleStickyScroll)
    }

    if (this.background) {
      window.removeEventListener('scroll', this.handleParallaxScroll)

      if (this.reducedMotionMq) {
        if (this.reducedMotionMq.removeEventListener) {
          this.reducedMotionMq.removeEventListener('change', this.onReducedMotionChange)
        } else if (this.reducedMotionMq.removeListener) {
          this.reducedMotionMq.removeListener(this.onReducedMotionChange)
        }
      }
    }
  },

  methods: {
    ...mapActions([ 'setFullscreen' ]),

    goBack () {
      if (this.backUrl) {
        this.$router.push(this.backUrl)
      } else {
        this.$router.go(-1)
      }
    },

    goFullscreen () {
      this.setFullscreen()
    },

    retractHeader () {
      this.retractActive = !this.retractActive
      !this.$media.mobile && this.calculateTitleFontSize()
      this.$emit('update-dimension-video', { headerIsRetracted: this.retractActive })
    },

    handleParallaxScroll () {
      if (!this.background || this.reducedMotion) return

      requestAnimationFrame(() => {
        const rect = this.$el.getBoundingClientRect()

        if (rect.bottom < 0 || rect.top > window.innerHeight) return

        const factor = this.$media && this.$media.mobile ? 0.1 : 0.175

        this.parallaxOffset = -window.scrollY * factor
      })
    },

    onReducedMotionChange (event) {
      this.reducedMotion = event.matches
    },

    calculateTitleFontSize () {
      if (this.retract) {
        let fontBase = 4
        const minFontSize = 1.6
        const maxHeight = this.retractActive ? 40 : 66

        this.$refs.title.style.fontSize = fontBase + 'em'

        while (this.$refs.title.scrollHeight > maxHeight) {
          if (fontBase <= minFontSize) return
          fontBase = (fontBase - 0.1).toFixed(1)
          this.$refs.title.style.fontSize = fontBase + 'em'
        }
      } else if (this.fitTitle) {
        let fontBase = 5
        const minFontSize = 1.6
        const maxHeight = 115

        this.$refs.title.style.fontSize = fontBase + 'em'

        while (this.$refs.title.scrollHeight > maxHeight) {
          if (fontBase <= minFontSize) return
          fontBase = (fontBase - 0.1).toFixed(1)
          this.$refs.title.style.fontSize = fontBase + 'em'
        }
      }
    },

    openMenu () {
      if (this.$route.params.session_uuid) {
        this.$parent.$parent.$parent.openMenu(true)
      } else {
        this.$parent.$parent.openMenu(true)
      }
    },

    handleStickyScroll () {
      this.stuck = window.scrollY > 0
    }
  }
}
</script>

<template>
  <header
    class="header"
    :class="{ 'has-background': background, 'has-title': title, 'has-bottom': $slots.tabs || fullscreen || retract, 'is-thick': thick, 'is-retracted': retractActive, 'is-fixed': fixed, 'is-sticky': sticky, 'is-stuck': sticky && stuck, 'is-float': float, 'light-theme': lightTheme, 'dark-theme': darkTheme }"
  >
    <div
      v-if="background"
      class="header-parallax"
      :style="parallaxStyle"
    />
    <div
      v-if="background"
      class="header-parallax-overlay"
    />
    <div
      v-if="$media.mobile"
      class="mobile-header"
    >
      <Action
        type="button"
        icon="menu"
        class="btn-menu"
        @click="openMenu()"
      />
      <span
        v-if="title"
        class="mobile-header-title"
      >{{ title }}</span>
      <Logo
        v-else-if="!classroom"
        logo
        :theme="$theme"
      />
      <Icon
        v-else
        name="local_library"
        class="icon-image"
      />

    </div>
    <div class="header-content">
      <div class="center">
        <slot
          v-if="!$media.mobile"
          name="actionbar"
        />
        <slot
          v-if="!$media.mobile"
          name="search"
        />
        <slot name="back">
          <Action
            v-if="back"
            type="button"
            :text="backText"
            icon="keyboard_backspace"
            class="btn-back"
            @click="goBack()"
          />
        </slot>
        <div
          v-if="tag || title || description || $slots.anything || $slots.progress"
          class="header-content-info"
        >
          <span
            v-if="tag"
            class="header-tag"
          >{{ tag }}</span>
          <h1
            v-if="title"
            ref="title"
            class="header-title"
            :title="title"
          >
            {{ title }}
          </h1>
          <p
            v-if="description"
            class="header-description"
          >
            {{ description }}
          </p>
          <slot name="progress" />
          <slot name="anything" />
        </div>
        <div
          v-if="$slots.buttons"
          class="header-form-buttons"
        >
          <slot name="buttons" />
        </div>
        <slot name="header-content" />
      </div>
    </div>
    <div
      v-if="$slots.tabs || fullscreen || retract"
      class="header-bottom"
    >
      <div class="center">
        <slot name="tabs" />
        <Action
          v-if="fullscreen && !$media.mobile"
          type="button"
          :icon="isFullscreen ? 'fullscreen_exit' : 'zoom_out_map'"
          class="btn-fullscreen"
          @click="goFullscreen"
        />
        <Action
          v-if="retract && !isFullscreen"
          type="button"
          :icon="retractActive ? 'keyboard_arrow_down' : 'keyboard_arrow_up'"
          class="btn-retract"
          @click="retractHeader"
        />
      </div>
    </div>
  </header>
</template>

<style src="@/assets/styles/themes/default/contentheader.css"></style>
